import { assign, createMachine, MachineConfig, MachineOptions, send } from 'xstate'

import { STATE_NODE_TAGS, TScrollOverflowMachineTypes } from './scrollOverflowMachineTypes'

type TContext = TScrollOverflowMachineTypes['context']
type TEvent = TScrollOverflowMachineTypes['events']
type TTypeState = TScrollOverflowMachineTypes['typeState']
type TStateSchema = TScrollOverflowMachineTypes['schema']

const machineConfig: MachineConfig<TContext, TStateSchema, TEvent> = {
  predictableActionArguments: true,
  id: 'scrollOverflow',
  initial: 'init',
  context: {
    contentElementRef: undefined,
    containerElementRef: undefined,
    scrollValue: 0,
    containerWidth: 0,
    contentWidth: 0,
  },
  on: {
    CALC_DIMENSIONS: { actions: ['calcDimensions', 'evaluateOverflow'] },
    TURN_ON: [
      { target: 'idle.end', cond: 'isEnd' },
      { target: 'idle.start', cond: 'isStart' },
      { target: 'idle.shifted', actions: ['setScrollValue'] },
    ],
    TURN_OFF: { target: '.off', actions: ['setScrollValue'] },
  },
  states: {
    init: {},
    idle: {
      on: {
        CENTER_CHILD: [
          { target: '.end', cond: 'isEnd' },
          { target: '.start', cond: 'isStart' },
          { target: '.shifted', actions: ['setScrollValue'] },
        ],
        SHIFT: [
          { target: '.end', cond: 'isEnd' },
          { target: '.start', cond: 'isStart' },
          { target: '.shifted', actions: ['setScrollValue'] },
        ],
      },
      initial: 'start',
      states: {
        start: { entry: 'setStartScrollValue', tags: [STATE_NODE_TAGS.rightShiftPossible] },
        end: { entry: 'setEndScrollValue', tags: [STATE_NODE_TAGS.leftShiftPossible] },
        shifted: { tags: [STATE_NODE_TAGS.leftShiftPossible, STATE_NODE_TAGS.rightShiftPossible] },
      },
    },
    off: {},
  },
}

const machineOptions: Partial<MachineOptions<TContext, TEvent>> = {
  actions: {
    //do the calculations of widths and positions of the elements
    calcDimensions: assign((context, event) => {
      if (
        !context.containerElementRef ||
        !context.contentElementRef ||
        !context.containerElementRef.current ||
        !context.contentElementRef.current ||
        event.type !== 'CALC_DIMENSIONS'
      ) {
        return {
          ...context,
        }
      }

      // width of the overflowing content (right edge of the last child element)
      const contentWidth = context.contentElementRef.current.scrollWidth

      // width of the container that specifies the boundaries within which the content will move
      const containerWidth = context.containerElementRef.current.offsetWidth

      if (!context.itemContainerElementId) {
        return {
          ...context,
          containerWidth,
          contentWidth,
          childElements: [],
        }
      }

      const itemContainer = document.querySelector<HTMLDivElement>(
        `#${context.itemContainerElementId}`,
      )

      if (!itemContainer) {
        return {
          ...context,
          containerWidth,
          contentWidth,
          childElements: [],
        }
      }

      // individual items (e.g. buttons or ware tiles)
      const childElements = [...itemContainer.children] as unknown as RoA<HTMLElement>

      // for each child element calculates the center position of the element and the value in px needed to center the element within the container
      const childElementsParams = childElements.map((el) => {
        return {
          center: el.offsetLeft + el.offsetWidth / 2,
          centerScrollValue: el.offsetLeft + el.offsetWidth / 2 - containerWidth / 2,
        }
      })

      return {
        ...context,
        containerWidth,
        contentWidth,
        childElements: childElementsParams,
      }
    }),

    //activate the  machine (send TURN_ON event) when the content is overflowing the container, deactivate it (send TURN_OFF) otherwise
    evaluateOverflow: send((context) => {
      return context.containerWidth < context.contentWidth
        ? { type: 'TURN_ON' }
        : { type: 'TURN_OFF' }
    }),

    //place the content to the position calculated based on event (and the passed value)
    setScrollValue: assign((context, event) => {
      // reset translateX
      if (event.type === 'TURN_OFF') {
        return {
          ...context,
          scrollValue: 0,
        }
      }

      // the content is moved by the passed shiftValue (px)
      if (event.type === 'SHIFT') {
        return {
          ...context,
          scrollValue: context.scrollValue + event.payload.shiftValue,
        }
      }

      if (!context.childElements || context.childElements.length === 0) {
        return {
          ...context,
        }
      }

      // the content is shifted so that the selected child is in the center of the container
      // check, if the `childIndex` is within the `childElements` range
      if (event.type === 'CENTER_CHILD' && context.childElements[event.payload.childIndex]) {
        return {
          ...context,
          scrollValue: context.childElements[event.payload.childIndex].centerScrollValue,
        }
      }

      return {
        ...context,
      }
    }),

    //position the content to the left edge of the container
    setStartScrollValue: assign<TContext, TEvent>({ scrollValue: 0 }),

    // position the content to the right edge of the container
    setEndScrollValue: assign<TContext, TEvent>((context) => {
      return {
        ...context,
        scrollValue: context.contentWidth - context.containerWidth,
      }
    }),
  },
  guards: {
    // evaluate whether the content should be placed on the right edge of the container
    isEnd: (context, event) => {
      // would a shift of the passed shiftValue cause a shift beyond the end of the content?
      if (event.type === 'SHIFT') {
        return (
          context.scrollValue + event.payload.shiftValue >
          context.contentWidth - context.containerWidth
        )
      }

      // would the content shift further than to the end after centering the selected element?
      if (event.type === 'CENTER_CHILD') {
        if (!context.childElements || context.childElements.length === 0) {
          return false
        }

        const element = context.childElements[event.payload.childIndex] ?? context.childElements[0]

        return (
          element.center - (context.contentWidth - context.containerWidth) >
          context.containerWidth / 2
        )
      }

      // is content currently shifted more that to the end
      return context.scrollValue >= context.contentWidth - context.containerWidth
    },

    // evaluate whether the content should be placed on the left edge of the container
    isStart: (context, event) => {
      // would a shift of the passed shiftValue cause a shift beyond the beginning of the content?
      if (event.type === 'SHIFT') {
        return context.scrollValue + event.payload.shiftValue <= 0
      }

      // would the content shift further than to the beginning after centering the selected element?
      if (event.type === 'CENTER_CHILD') {
        if (!context.childElements || context.childElements.length === 0) {
          return false
        }

        const element = context.childElements[event.payload.childIndex] ?? context.childElements[0]

        return element.centerScrollValue <= 0
      }

      // is content currently moved further beyond the beginning
      return context.scrollValue <= 0
    },
  },
}

export const scrollOverflowMachine = createMachine<TContext, TEvent, TTypeState>(
  machineConfig,
  machineOptions,
)
